import React, { useEffect, useState, useRef } from "react";
import * as Mui from "@mui/material";
import moment from "moment";
import { useSelector } from "react-redux";
import { showToast } from "services/utils/Status";
import { checkEditorPermission } from "services/utils/HelperFunction";
import { scrollTop } from "services/constants";
import CircleIcon from "@mui/icons-material/Circle";
import { TableEmptyState } from "components/CustomComponents/EmptyState";
import { Loader } from "components/Loader";
import sessionHandling from "services/utils/notificationUtils";
import {
  designColors,
  designRadii,
  designShell,
  paginationFooter,
  rowsPerPageOptions,
} from "services/constants/designTokens";
import { GlobalConfirmDialog } from "components/DesignSystem/sharedUiPatterns";
import { canonicalLeaveStatus, leaveStatusVisual } from "pages/TeamZone/Approve/Leave/LeaveStatusCards";

const StatusChip = ({ status }) => {
  const visual = leaveStatusVisual(status);
  return (
    <Mui.Chip
      size="small"
      icon={
        <CircleIcon sx={{ fontSize: "9px !important", color: `${visual.accent} !important` }} />
      }
      label={canonicalLeaveStatus(status) || "N/A"}
      sx={{
        height: 24,
        borderRadius: designRadii.pill,
        backgroundColor: visual.tint,
        color: visual.accent,
        "& .MuiChip-label": { px: 1, fontSize: 12, fontWeight: 600 },
      }}
    />
  );
};

const cellSx = {
  py: 1,
  fontSize: 13,
  color: designColors.slate800,
  borderBottom: `1px solid ${designColors.slate100}`,
  whiteSpace: "nowrap",
};

// Same "DD-MM-YYYY" date format as LeaveBalanceDetailPanel.js — newest
// first regardless of the order the backend sends.
const sortByDateDesc = (items) =>
  [...(items || [])].sort(
    (a, b) =>
      moment(b?.date, "DD-MM-YYYY").valueOf() -
      moment(a?.date, "DD-MM-YYYY").valueOf(),
  );

export const PermissionsDetails = ({
  selectedUser,
  loadPermissionApi,
  setLoadPersmissionApi,
  taxYearStartDateUser,
  taxYearEndDateUser,
}) => {
  const requestCompanyId = useSelector((state) => state.tokenReducer.topbarCompanyId);
  const { token, domain, globalSearchValue, userScreenAccess } = useSelector(
    (state) => state.tokenReducer,
  );
  const hasEditAccess = checkEditorPermission(
    userScreenAccess?.module_details,
    "Leave Balances",
    "Leave Balances",
  );
  const [cancelReason, setCancelReason] = useState("");
  const [cancelReasonValidation, setCancelReasonValidation] = useState("");
  const [loading, setLoading] = useState(true);
  const [permissionDetails, setPerMissionDetails] = useState([]);
  const [leavePage, setLeavePage] = useState(0);
  const [leaverowsPerPage, setLeaveRowsPerPage] = useState(rowsPerPageOptions[0]);
  const [masterDataForSearch, setMasterDataForSearch] = useState([]);
  const [cancelPermissionConfirmation, setCancelPermissionConfirmation] = useState(null);
  const [buttonLoader, setButtonLoader] = useState(false);
  const containerRef = useRef(null);

  useEffect(() => {
    const getUserDetails = async () => {
      try {
        const response = await fetch(
          `${domain}individual_permisson_request/?user_id=${selectedUser.user_id}&start_date=${taxYearStartDateUser}&end_date=${taxYearEndDateUser}&company_id=${requestCompanyId}`,
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
              Authorization: `token ${token}`,
            },
          },
        );
        const res = await response.json();
        if (response.ok) {
          const permissionList = sortByDateDesc(
            Array.isArray(res)
              ? res
              : Array.isArray(res?.results)
                ? res.results
                : [],
          );
          setLoading(false);
          setPerMissionDetails(permissionList);
          setMasterDataForSearch(permissionList);
        } else if (response.status === 409) {
          sessionHandling();
        } else {
          throw new Error("REQUEST_FAILED");
        }
      } catch (error) {
        console.error("[Permissiondetails] operation failed:", error);
        showToast("error", "Couldn't load permission details. Please refresh the page.");
      }
    };
    // Also fetch on `loading` (true on a fresh mount) rather than
    // loadPermissionApi alone — toggling the panel's expand/collapse swaps
    // its wrapper between Mui.Drawer and a portal, which are different
    // element types at the same tree position, so React fully remounts
    // this component. That resets `loading` back to true, but
    // loadPermissionApi (owned by the parent) was already consumed from
    // the first load, so without this the spinner would never resolve.
    if (loadPermissionApi || loading) {
      getUserDetails();
      setLoadPersmissionApi(false);
    }
  }, [
    domain,
    loadPermissionApi,
    loading,
    selectedUser?.id,
    selectedUser?.user_id,
    setLoadPersmissionApi,
    taxYearEndDateUser,
    taxYearStartDateUser,
    token, requestCompanyId
  ]);

  const handleChangeLeaveRowsPerPage = (event) => {
    setLeaveRowsPerPage(parseInt(event.target.value, 10));
    setLeavePage(0);
  };

  const cancelPermissionRequest = async (permissionId) => {
    if (!hasEditAccess) return;
    if (cancelReason?.trim().length >= 5 && cancelReason !== "") {
      setButtonLoader(true);
      try {
        const response = await fetch(`${domain}permission_cancel/`, {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `token ${token}`,
          },
          body: JSON.stringify({
            id: permissionId,
            reason: cancelReason,
          }),
        });
        await response.json();
        if (response.ok) {
          setLoadPersmissionApi(true);
          handleCancelCancel();
        } else if (response.status === 409) {
          sessionHandling();
        } else {
          throw new Error("REQUEST_FAILED");
        }
      } catch (error) {
        console.error("[Permissiondetails] operation failed:", error);
        showToast("error", "Couldn't load permission details. Please refresh the page.");
      } finally {
        setButtonLoader(false);
      }
    } else {
      setCancelReasonValidation(
        cancelReason === "" || !cancelReason.trim() ? "Reason" : "Less then 5 charactor",
      );
    }
  };

  const handleCancelConfirmation = (permission) => {
    if (!hasEditAccess) return;
    setCancelPermissionConfirmation(permission);
  };

  const handleCancelCancel = () => {
    setCancelPermissionConfirmation(null);
    setCancelReason("");
    setCancelReasonValidation("");
  };

  useEffect(() => {
    const filteredResults = masterDataForSearch.filter((item) =>
      ["date", "status"].some((key) =>
        item[key]?.toString().toLowerCase().includes(globalSearchValue.toString().toLowerCase()),
      ),
    );
    setPerMissionDetails(filteredResults);
  }, [globalSearchValue, masterDataForSearch]);

  useEffect(() => {
    scrollTop(containerRef);
  }, [leavePage]);

  if (loading) {
    return (
      <Mui.Box sx={{ py: 6 }}>
        <Loader info="Loading..." />
      </Mui.Box>
    );
  }

  return (
    <React.Fragment>
      <Mui.TableContainer ref={containerRef} sx={{ overflowX: "auto" }}>
        <Mui.Table size="small" sx={{ minWidth: 640 }}>
          <Mui.TableHead>
            <Mui.TableRow>
              <Mui.TableCell sx={{ ...designShell.tableHeadCellBase, py: 1, whiteSpace: "nowrap" }}>Date</Mui.TableCell>
              <Mui.TableCell sx={{ ...designShell.tableHeadCellBase, py: 1, whiteSpace: "nowrap" }}>Time</Mui.TableCell>
              <Mui.TableCell sx={{ ...designShell.tableHeadCellBase, py: 1, whiteSpace: "nowrap" }}>Duration</Mui.TableCell>
              <Mui.TableCell sx={{ ...designShell.tableHeadCellBase, py: 1, whiteSpace: "nowrap" }}>Status</Mui.TableCell>
              {hasEditAccess && (
                <Mui.TableCell sx={{ ...designShell.tableHeadCellBase, py: 1, whiteSpace: "nowrap" }}>Actions</Mui.TableCell>
              )}
              <Mui.TableCell sx={{ ...designShell.tableHeadCellBase, py: 1, whiteSpace: "nowrap" }}>Description</Mui.TableCell>
            </Mui.TableRow>
          </Mui.TableHead>
          <Mui.TableBody>
            {permissionDetails?.length === 0 ? (
              <TableEmptyState dataLength={masterDataForSearch} colSpan={hasEditAccess ? 6 : 5} />
            ) : (
              permissionDetails
                .slice(
                  leavePage * leaverowsPerPage,
                  leavePage * leaverowsPerPage + leaverowsPerPage,
                )
                .map((permission) => (
                  <Mui.TableRow key={permission.id} sx={designShell.tableRowHover}>
                    <Mui.TableCell sx={cellSx}>{permission.date}</Mui.TableCell>
                    <Mui.TableCell sx={cellSx}>
                      {permission.start_time} - {permission.end_time}
                    </Mui.TableCell>
                    <Mui.TableCell sx={cellSx}>
                      {permission.total_time?.split(":")[0]}h {permission.total_time?.split(":")[1]}m
                    </Mui.TableCell>
                    <Mui.TableCell sx={cellSx}>
                      <StatusChip status={permission.status} />
                    </Mui.TableCell>
                    {hasEditAccess && (
                      <Mui.TableCell sx={cellSx}>
                        <Mui.Button
                          size="small"
                          disabled={!["Pending", "Approved"].includes(permission.status)}
                          sx={{
                            minWidth: 0,
                            px: 1,
                            fontSize: 12,
                            fontWeight: 600,
                            textTransform: "none",
                            color: "#DC2626",
                          }}
                          onClick={() => handleCancelConfirmation(permission.id)}
                        >
                          Cancel
                        </Mui.Button>
                      </Mui.TableCell>
                    )}
                    <Mui.TableCell
                      sx={{ ...cellSx, maxWidth: 220, overflow: "hidden", textOverflow: "ellipsis" }}
                    >
                      <Mui.Tooltip
                        title={
                          permission.status === "Rejected" && permission.reason
                            ? `${permission.description || "N/A"} — Rejection reason: ${permission.reason}`
                            : permission.description || ""
                        }
                      >
                        <span>{permission.description || "N/A"}</span>
                      </Mui.Tooltip>
                    </Mui.TableCell>
                  </Mui.TableRow>
                ))
            )}
          </Mui.TableBody>
        </Mui.Table>
      </Mui.TableContainer>

      {permissionDetails.length > leaverowsPerPage ? (
        <Mui.Box sx={{ pt: 1.5 }}>
          {paginationFooter({
            page: leavePage,
            rowsPerPage: leaverowsPerPage,
            total: permissionDetails.length,
            itemLabel: "permissions",
            onPageChange: setLeavePage,
            onRowsPerPageChange: handleChangeLeaveRowsPerPage,
            rowsPerPageOptions,
          })}
        </Mui.Box>
      ) : null}

      <GlobalConfirmDialog
        open={hasEditAccess && Boolean(cancelPermissionConfirmation)}
        title="Cancel Permission"
        onCancel={handleCancelCancel}
        onConfirm={() => cancelPermissionRequest(cancelPermissionConfirmation)}
        confirmLabel="Yes, Cancel"
        cancelLabel="No"
        loading={buttonLoader}
      >
        <Mui.TextField
          fullWidth
          multiline
          minRows={3}
          placeholder="Enter the permission cancel reason"
          onChange={(e) => setCancelReason(e.target.value)}
          error={["Reason", "Less then 5 charactor"].includes(cancelReasonValidation)}
          helperText={
            cancelReasonValidation === "Reason"
              ? "Please enter the cancel reason"
              : cancelReasonValidation === "Less then 5 charactor"
                ? "Minimum 5 characters with no spaces"
                : " "
          }
        />
      </GlobalConfirmDialog>
    </React.Fragment>
  );
};
